import { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useSupportTicketMessage } from '../../../../composables/support-ticket/useSupportTicketMessage';

interface MessageThreadProps {
  code: string;
  newMessage?: string;
}

const MessageThread = ({ code, newMessage = '' }: MessageThreadProps) => {
  useEffect(() => {
    setAllMessages(prev => [...prev, newMessage]);
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

  }, [newMessage]);
  const [page, setPage] = useState<number>(1);
  const [allMessages, setAllMessages] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const prevMessagesRef = useRef<any[]>([]);

  const { data: messages, isLoading } = useSupportTicketMessage({ code, page });

  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if container has scroll
  const checkScroll = useCallback(() => {
    if (containerRef.current) {
      const hasVerticalScroll = containerRef.current.scrollHeight > containerRef.current.clientHeight;
      setHasScroll(hasVerticalScroll);
    }
  }, []);

  // Prepend older messages when page > 1, or append new messages on page 1
  useEffect(() => {
    if (!messages?.items) return;

    if (page === 1) {
      // If we're on page 1 and there are more messages than before, append new ones
      if (prevMessagesRef.current.length > 0 && messages.items.length > prevMessagesRef.current.length) {
        // New messages arrived, append them to bottom
        const newMessages = messages.items.filter((msg: any) => !prevMessagesRef.current.find(p => p.uuid === msg.uuid));
        setAllMessages(prev => [...prev, ...newMessages]);
      } else {
        // Initial load or reset
        setAllMessages([...messages.items].reverse());
      }

      prevMessagesRef.current = messages.items;
    } else {
      // Loading older pages, prepend them
      setAllMessages(prev => [...[...messages.items].reverse(), ...prev]);
    }
    setLoadingMore(false);
    setTimeout(checkScroll, 0);
  }, [messages, page, checkScroll]);

  // Auto scroll to bottom only if user is already at bottom
  useEffect(() => {
    if (page === 1 && isUserAtBottom) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages.length, page, isUserAtBottom]);

  // Track if user is at bottom
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    // Check if user is at bottom (within 100px)
    const isAtBottom = containerRef.current.scrollHeight - containerRef.current.scrollTop - containerRef.current.clientHeight < 100;
    setIsUserAtBottom(isAtBottom);
  }, []);


  useEffect(() => {
    const div = containerRef.current;
    div?.addEventListener('scroll', handleScroll);
    return () => div?.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="box-shadow rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-foreground">Conversation</h3>
      </div>

      {/* Messages */}

      <div
        ref={containerRef}
        className="max-h-[500px] overflow-y-auto p-5 space-y-4 flex flex-col"
      >
        {(messages?.total === 0 && newMessage === '') && (
          <div className="text-center text-sm text-muted-foreground py-2">
            No messages
          </div>
        )}
        {isLoading && page === 1 && (
          <div className="text-center text-sm text-muted-foreground py-2">
            Loading messages...
          </div>
        )}

        {loadingMore && (
          <div className="text-center text-sm text-muted-foreground py-2">
            Loading older messages...
          </div>
        )}

        {!isLoading && (
          <>
            {messages?.hasMorePages && !loadingMore && (
              <button
                onClick={() => {
                  setLoadingMore(true);
                  setPage(prev => prev + 1);
                }}
                className="w-full text-center text-sm text-primary hover:text-primary/80 py-2 font-medium mb-4"
              >
                Load older messages
              </button>
            )}

            {allMessages.map((msg: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.is_agent ? 'flex-row-reverse' : 'flex-row'
                  }`}
              >
                {/* Avatar */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center uppercase rounded-full bg-primary/10 text-xs font-medium text-primary">
                  {msg.username?.[0] || 'U'}
                </div>

                {/* Message */}
                <div className="max-w-[70%] min-w-0">
                  <div
                    className={`rounded-2xl p-3 ${msg.is_agent
                      ? 'bg-primary text-white rounded-tr-md'
                      : 'bg-secondary text-white rounded-tl-md'
                      }`}
                  >
                    <p className="text-sm leading-relaxed">{msg.message}</p>
                  </div>

                  <div
                    className={`flex items-center gap-2 mt-1.5 ${msg.is_agent ? 'justify-end' : ''
                      }`}
                  >
                    <span className="text-[11px] text-muted-foreground">
                      {msg.username}
                    </span>
                    <span className="text-[11px] text-muted-foreground">•</span>
                    <span className="text-[11px] text-muted-foreground">
                      {moment(msg.created_at).fromNow()}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
};

export default MessageThread;
